import {
  StripeElementsOptions,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { ePlanFrequency } from 'src/providers/SubscriptionProvider/Subscription.enums';
import { eCardBrand } from './SubscriptionManager.enums';
import {
  tCancellationReason,
  tCancellationReasonCodes,
  tPaymentMethod,
  tPlanToBuy,
  tUseSubscriptionManagerContext,
} from './SubscriptionManager.types';

export const TRIAL_PLAN: tPlanToBuy = {
  emoji: '🪄',
  preferred: false,
  translateKey: 'Trial',
};

export const FULL_PLAN: tPlanToBuy = {
  emoji: '🎉',
  preferred: true,
  translateKey: 'FullPlan',
};

export const STRIPE_ELEMENTS_CONFIG: StripeElementsOptions = {
  mode: 'setup',
  currency: 'mxn',
  paymentMethodCreation: 'manual',
};

export const PAYMENT_ELEMENT_OPTIONS: StripePaymentElementOptions = {
  fields: {
    billingDetails: {
      name: 'auto',
    },
  },
  defaultValues: {
    billingDetails: {
      name: 'César',
    },
  },
};

export const INITIAL_PAYMENT_METHOD: tPaymentMethod = {
  paymentMethodId: '',
  lastDigits: '',
  brand: eCardBrand.VISA,
  type: '',
  name: '',
  year: 0,
  month: 0,
  createdAt: null,
};

export const CENTS_TO_DOLLARS_CONVERSION_FACTOR = 100;

// Multiply a value expressed in currency-native units (MXN, USD, …) to convert it
// into cents, the unit Stripe and our pricing math use everywhere. Named explicitly
// so call sites that multiply read clearly without inverting the meaning of
// CENTS_TO_DOLLARS_CONVERSION_FACTOR (which is meant for division — cents → dollars).
export const CURRENCY_UNITS_TO_CENTS_FACTOR = 100;

// Per-call axios timeout for endpoints that may hold open during Stripe 3D Secure
// challenges (issuer-side challenge UI can run past the global 30s singleton cap).
// An ECONNABORTED with the backend still processing risks a ghost-charge / split-state
// subscription if the user retries. Backend MUST treat these endpoints as idempotent
// (retrying a `payment_intent_id` should not double-charge) — this client-side budget
// is a UX safety margin, not a substitute for that contract.
export const STRIPE_TIMEOUT_MS = 60_000;

// Lead Limit Addon increment value (in conversations)
export const LEAD_LIMIT_ADDON_INCREMENT = 15000;

/**
 * The ONE map from a cancellation reason to what the retention flow sends and
 * records: the `reason_code` leadbilling resolves the offer from, and the
 * hypothesis code every `front.settings.retention_flow_*` event carries. Two
 * maps used to exist and disagreed (the reason step reported price as `h2`
 * while the offer and outcome events reported it as `h5`), so no funnel could
 * join its steps by reason. The hypothesis is the number of the API code.
 *
 * Which offer a reason gets is NOT decided here: the server answers it.
 */
export const CANCELLATION_REASON_CODES: Record<
  tCancellationReason,
  tCancellationReasonCodes
> = {
  pause_operation: {
    apiCode: 'H8_business_pause',
    hypothesis: 'h8',
    analyticsLabel: 'Pausaré la operación de mi empresa temporalmente',
  },
  price_too_high: {
    apiCode: 'H5_high_pricing',
    hypothesis: 'h5',
    analyticsLabel: 'El precio es muy alto para mi presupuesto actual',
  },
  technical_problems: {
    apiCode: 'H2_technical_issues',
    hypothesis: 'h2',
    analyticsLabel: 'He tenido problemas técnicos con la plataforma',
  },
  plan_not_suitable: {
    apiCode: 'H6_plan_fit',
    hypothesis: 'h6',
    analyticsLabel: 'El plan que contraté no se adapta a mis necesidades',
  },
  not_seeing_results: {
    apiCode: 'H4_lack_of_results',
    hypothesis: 'h4',
    analyticsLabel: 'No estoy viendo los resultados que esperaba',
  },
  other: {
    apiCode: 'OTHER',
    hypothesis: 'other',
    analyticsLabel: 'Otro motivo',
  },
};

/**
 * Refusal codes that mean "only the workspace Owner can change billing".
 * leadbilling answers `owner_required`; bff-web relays it, or its status
 * fallback `BILLING_FORBIDDEN` when the code does not cross.
 */
export const OWNER_REQUIRED_REFUSAL_CODES: readonly string[] = [
  'owner_required',
  'BILLING_FORBIDDEN',
];

export const SUBSCRIPTION_MANAGER_INITIAL_STATE: tUseSubscriptionManagerContext =
  {
    availableProductsForPurchase: [],
    selectedAddonProducts: [],
    addSelectedAddonProduct: () => {},
    removeSelectedAddonProduct: () => {},
    toggleSelectedAddonProduct: () => {},
    plans: null,
    checkoutSelectedPlan: () => false,
    showCheckoutView: false,
    showPlansView: false,
    togglePlansView: () => false,
    planToBuy: null,
    showAddPaymentMethodModal: false,
    toggleAddPaymentMethodModal: () => false,
    openAddPaymentMethodModal: () => false,
    addPaymentMethodOptions: null,
    cardSavedForCaller: { token: 0, requestId: null, paymentMethodId: null },
    addPaymentMethod: () => new Promise(() => false),
    addingPaymentMethod: false,
    cardSavedAsDefaultToken: 0,
    cardSavedAsDefaultPaymentMethodId: null,
    cardSavedWithoutDefaultToken: 0,
    cardSavedWithoutDefaultPaymentMethodId: null,
    showPaymentMethodsManager: false,
    editPaymentMethods: () => false,
    setExtraSeats: () => false,
    extraSeats: 0,
    onCreateSubscription: () => false,
    onUpgradeSubscription: () => false,
    handlePayment: () => false,
    returnToOverview: () => false,
    returnFromCheckout: () => false,
    updateWatermarkPlan: () => false,
    updateSeatsPlan: () => false,
    updatingPlan: false,
    processingPayment: false,
    selectedPaymentMethod: INITIAL_PAYMENT_METHOD,
    paymentMethods: [],
    paymentMethodsLoadState: 'loading',
    reloadPaymentMethods: () => Promise.resolve(),
    trialChargePaymentMethodId: null,
    paymentMethodDefaults: {},
    setSelectedPaymentMethodOnClick: () => false,
    loadingPaymentMethodRequest: false,
    payDebts: () => false,
    deleteCard: () => false,
    showPaymentCompletedModal: false,
    togglePaymentCompletedModal: () => false,
    cancellingSubscription: false,
    cancelSubscriptionFormik: undefined,
    showCancelSubscriptionModal: false,
    toggleCancelSubscriptionModal: () => false,
    reactivatePlan: () => {},
    couponCode: '',
    setCouponCode: () => {},
    appliedCouponDiscount: null,
    couponDiscountType: null,
    couponDuration: null,
    couponDurationInMonths: null,
    couponError: null,
    setCouponError: () => {},
    applyingCoupon: false,
    applyCoupon: () => Promise.resolve(),
    removeCoupon: () => {},
    isUpgradeContext: false,
    filteredAddOns: [],
    getSeatPriceFromPlans: () => 0,
    showManageUsersModal: false,
    toggleManageUsersModal: () => false,
    showManageWatermarkModal: false,
    toggleManageWatermarkModal: () => false,
    showManageAdvancedLeadbotModal: false,
    toggleManageAdvancedLeadbotModal: () => false,
    updateAdvancedLeadbotPlan: () => false,
    showManageLeadLimitAddonModal: false,
    toggleManageLeadLimitAddonModal: () => false,
    updateLeadLimitAddonPlan: () => false,
    isTrial: false,
    setIsTrial: () => false,
    products: null,
    monthlyCredits: null,
    setMonthlyCredits: () => null,
    baseMonthlyCredits: 250,
    showTrialCheckoutView: false,
    toggleTrialCheckoutView: () => false,
    paymentMethodsOptions: [],
    showOnlyWAPIPlans: false,
    period: ePlanFrequency.MONTHLY,
    setPeriod: () => ePlanFrequency.MONTHLY,
    convertTrialToSubscription: () => new Promise(() => false),
    invoicePreview: null,
    loadingInvoicePreview: false,
    fetchInvoicePreview: () => new Promise(() => false),
    generalCancellationProperties: {
      user_email: '',
      user_plan: '',
      user_plan_recurrency_type: '',
      user_mrr: 0,
      workspace_id: '',
      subscription_status: '',
    },
    resetCancellationModalStates: () => {},
  };

/**
 * Meses que cubre cada ciclo de cobro (RET-1961, regla `SA-08`).
 *
 * El catálogo guarda el saldo incluido POR MES y es el mismo en todas las
 * periodicidades (`SA-11`); lo que cambia es la entrega, que ocurre una vez por
 * ciclo. Quien muestre un monto de saldo lo escala con esto.
 *
 * Sin entrada no se pinta nada: si no sabemos cuántos meses cubre el ciclo, no
 * sabemos cuánto se entrega ni con qué cadencia decirlo, y eso no se adivina.
 * Una periodicidad nueva en el catálogo llega con su copy o no llega.
 */
export const MONTHS_PER_CYCLE: Record<string, number | undefined> = {
  [ePlanFrequency.MONTHLY]: 1,
  [ePlanFrequency.QUARTERLY]: 3,
  [ePlanFrequency.BIANNUAL]: 6,
  [ePlanFrequency.ANNUALLY]: 12,
};

/**
 * BUG-861: deep link that opens the Leadbot Levels add-on modal on arrival at
 * the Subscription tab. The reactivation gate in Automatizaciones -> Leadbot
 * -> Tiempos is the producer; without landing the customer inside the modal
 * the CTA only drops them on the tab to hunt for the card, which is the dead
 * end that gate exists to remove. Lives here, with the surface that consumes
 * it, so the two sides cannot drift.
 */
export const OPEN_ADVANCED_LEADBOT_ADDON_PARAM = 'openAdvancedLeadbotAddon';

/**
 * Minimum characters the cancellation comment needs before the flow may continue.
 * Shared so the Yup schema that enforces it and the counter that reports it cannot
 * drift: a counter that says "ready" while validation still refuses is worse than
 * no counter at all.
 */
export const CANCELLATION_DESCRIPTION_MIN_LENGTH = 30;

/**
 * RET-1974: how long the return from a bank redirect waits for Stripe.js and
 * the subscription summary before it stops waiting (KTD8).
 */
export const REDIRECT_RETURN_GATE_TIMEOUT_MS = 15_000;
